import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useRolesPermissionAccess } from "../hooks";

// ─────────────────────────────────────────────────────────────────────────────
// DEV PERMISSION BYPASS
// Set to `true` to skip ALL backend permission checks during UI development.
// Every route will be accessible and every `can*` check will return true.
// ⚠️  Set back to `false` before merging / releasing to production.
// ─────────────────────────────────────────────────────────────────────────────
const DEV_BYPASS_PERMISSIONS = false;
export const usePermissions = () => {
  const location = useLocation();
  // const data= ROLE_PERMISSIONS;
  const remoteUtilityId = getRemoteUtilityId();
  const { data, isLoading, error, refetch } = useRolesPermissionAccess(
    String(remoteUtilityId),
  );
  const permissions = useMemo(() => data?.permissions.modules || {}, [data]);
  const accessibleRoutes = useMemo(() => data?.accessibleRoutes || [], [data]);
  const userInfo = useMemo(() => data?.userInfo, [data]);
  // Route access check
  const hasRouteAccess = (route: string): boolean => {
    if (DEV_BYPASS_PERMISSIONS) return true; // 🔧 DEV: bypass enabled
    if (!accessibleRoutes.length) return false;
    const normalizeRoute = (r: string) =>
      r.replace(/^\/+|\/+$/g, "").toLowerCase();
    const normalizedRoute = normalizeRoute(route);
    const normalizedAccessibleRoutes = accessibleRoutes.map(normalizeRoute);
    return normalizedAccessibleRoutes.some((accessibleRoute) => {
      // exact match
      if (normalizedRoute === accessibleRoute) return true;
      // check param patterns
      const regex = new RegExp(
        "^" + accessibleRoute.replace(/:[^/]+/g, "[^/]+") + "$",
      );
      return regex.test(normalizedRoute);
    });
  };
  // Check current route access
  const hasCurrentRouteAccess = (): boolean => {
    if (DEV_BYPASS_PERMISSIONS) return true; // 🔧 DEV: bypass enabled
    return hasRouteAccess(location.pathname);
  };
  // Module detection from current route
  const getCurrentModule = (): string | null => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/cx/")) return "CX";
    if (path.includes("/mx/")) return "MX";
    if (path.includes("/bx/")) return "BX";
    if (path.includes("/wx/")) return "WX";
    if (path.includes("/ux/")) return "UX";
    if (path.includes("/call-center/")) return "CALL_CENTER";
    if (path.includes("/onboarding/")) return "OX";
    if (path.includes("/integration-hub/")) return "INTEGRATION";
    if (path.includes("/erp-integration/")) return "INTEGRATION";
    return null;
  };
  // Permission checks
  const can = (
    action:
      | "read"
      | "create"
      | "update"
      | "delete"
      | "export"
      | "connect"
      | "configure",
    moduleCode?: string,
    subModule?: string,
  ): boolean => {
    if (DEV_BYPASS_PERMISSIONS) return true; // 🔧 DEV: bypass enabled
    if (!moduleCode) {
      // Use current module if not specified
      moduleCode = getCurrentModule() || undefined;
      if (!moduleCode) return false;
    }
    const module = permissions[moduleCode];
    if (!module) return false;
    if (subModule) {
      const subModulePerms = module[subModule];
      return subModulePerms ? subModulePerms[`can_${action}`] || false : false;
    }
    // Check if any submodule in the module has this permission
    return Object.values(module).some(
      (subModulePerms) => subModulePerms[`can_${action}`],
    );
  };
  // Simple permission checks (these automatically use current module)
  const canCreate = (module?: string, subModule?: string) =>
    can("create", module, subModule);
  const canEdit = (module?: string, subModule?: string) =>
    can("update", module, subModule);
  const canDelete = (module?: string, subModule?: string) =>
    can("delete", module, subModule);
  const canExport = (module?: string, subModule?: string) =>
    can("export", module, subModule);
  const canView = (module?: string, subModule?: string) =>
    can("read", module, subModule);
  const canConnect = (module?: string, subModule?: string) =>
    can("connect", module, subModule);
  const canConfigure = (module?: string, subModule?: string) =>
    can("configure", module, subModule);
  // Role checks
  const getRoles = (): string[] => userInfo?.role_codes || [];
  const hasRole = (role: string): boolean => getRoles().includes(role);
  const isTenantAdmin = (): boolean => hasRole("Tenant Admin");
  return {
    // Data
    permissions,
    accessibleRoutes,
    userInfo,
    isLoading, // Add this
    error, // Add this
    // Route checks
    hasRouteAccess,
    hasCurrentRouteAccess,
    getCurrentModule,
    // Permission checks
    can,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    canView,
    canConnect,
    canConfigure,
    // Role checks
    getRoles,
    hasRole,
    isTenantAdmin,
  };
};
